import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DistanceCalculatorService {

  private readonly EARTH_RADIUS_A = 6378137; // Semi-major axis in meters
  private readonly EARTH_FLATTENING = 1 / 298.257223563; // Flattening
  private readonly EARTH_RADIUS_B = (1 - this.EARTH_FLATTENING) * this.EARTH_RADIUS_A; // Semi-minor axis in meters

  calculateDistanceMeters(
    coords1: [number | string, number | string],
    coords2: [number | string, number | string],
    useVincenty = true
  ): number | null {
    const point1 = this.validateCoordinates(coords1);
    const point2 = this.validateCoordinates(coords2);

    

    if (!point1 || !point2) return null;
    const [lat1, lon1] = point1;
    const [lat2, lon2] = point2;
    try {
      return useVincenty
        ? this.vincentyDistanceMeters(lat1, lon1, lat2, lon2)
        : this.haversineDistanceMeters(lat1, lon1, lat2, lon2);
    } catch {
      return this.haversineDistanceMeters(lat1, lon1, lat2, lon2);
    }
  }

  private validateCoordinates(coords: [number | string, number | string]): [number, number] | null {
    if (!Array.isArray(coords) || coords.length !== 2) return null;
    const lat = typeof coords[0] === 'string' ? parseFloat(coords[0]) : coords[0];
    const lon = typeof coords[1] === 'string' ? parseFloat(coords[1]) : coords[1];
    if (
      typeof lat === 'number' &&
      typeof lon === 'number' &&
      !isNaN(lat) &&
      !isNaN(lon) &&
      lat >= -90 && lat <= 90 &&
      lon >= -180 && lon <= 180
    ) return [lat, lon];
    return null;
  }


  private vincentyDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const lat1Rad = this.toRadians(lat1);
    const lat2Rad = this.toRadians(lat2);
    const lonDiffRad = this.toRadians(lon2 - lon1);
    const reducedLatitude1  = Math.atan((1 - this.EARTH_FLATTENING) * Math.tan(lat1Rad));
    const reducedLatitude2 = Math.atan((1 - this.EARTH_FLATTENING) * Math.tan(lat2Rad));
    const sinU1 = Math.sin(reducedLatitude1 ), cosU1 = Math.cos(reducedLatitude1);
    const sinU2 = Math.sin(reducedLatitude2), cosU2 = Math.cos(reducedLatitude2);
    let lambda = lonDiffRad;
    let lambdaPrev: number;
    let iterLimit = 100;
    let cosAlpha = 0, cos2SigmaMid = 0, sigma = 0, sinSigma = 0, cosSigma = 0;
    do {
      const sinLambda = Math.sin(lambda), cosLambda = Math.cos(lambda);
      sinSigma = Math.sqrt(
        (cosU2 * sinLambda) ** 2 +
        (cosU1 * sinU2 - sinU1 * cosU2 * cosLambda) ** 2
      );
      if (sinSigma === 0) return 0; 
      cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda;
      sigma = Math.atan2(sinSigma, cosSigma);
      const sinAlpha = cosU1 * cosU2 * sinLambda / sinSigma;
      cosAlpha = 1 - sinAlpha ** 2;
      cos2SigmaMid = cosSigma - 2 * sinU1 * sinU2 / (cosAlpha || 1);
      const longitudeFactor = this.EARTH_FLATTENING / 16 * cosAlpha * (4 + this.EARTH_FLATTENING * (4 - 3 * cosAlpha));
      lambdaPrev = lambda;
      lambda = lonDiffRad + (1 - longitudeFactor) * this.EARTH_FLATTENING * sinAlpha *
        (sigma + longitudeFactor * sinSigma * (cos2SigmaMid + longitudeFactor * cosSigma * (-1 + 2 * cos2SigmaMid ** 2)));
    } while (Math.abs(lambda - lambdaPrev) > 1e-12 && --iterLimit > 0);
    if (iterLimit === 0) return this.haversineDistanceMeters(lat1, lon1, lat2, lon2);
    const reducedFactor = cosAlpha * (this.EARTH_RADIUS_A ** 2 - this.EARTH_RADIUS_B ** 2) / this.EARTH_RADIUS_B ** 2;
    const geoCorrectionCoeff = 1 + reducedFactor / 16384 * (4096 + reducedFactor * (-768 + reducedFactor * (320 - 175 * reducedFactor)));
    const geoCorrectionCoeffS = reducedFactor / 1024 * (256 + reducedFactor * (-128 + reducedFactor * (74 - 47 * reducedFactor)));
    const deltaSigma = geoCorrectionCoeffS * sinSigma * (cos2SigmaMid + geoCorrectionCoeffS / 4 * (cosSigma * (-1 + 2 * cos2SigmaMid ** 2)
      - geoCorrectionCoeffS / 6 * cos2SigmaMid * (-3 + 4 * sinSigma ** 2) * (-3 + 4 * cos2SigmaMid ** 2)));
    return this.EARTH_RADIUS_B * geoCorrectionCoeff * (sigma - deltaSigma); 
  }
  
  private haversineDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const AverageRadius = 6371000; // meters 
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    const lat1Rad = this.toRadians(lat1);
    const lat2Rad = this.toRadians(lat2);
    const haversineForm = Math.sin(dLat / 2) ** 2 +
              Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(dLon / 2) ** 2;
    const centralAngleRad  = 2 * Math.atan2(Math.sqrt(haversineForm), Math.sqrt(1 - haversineForm));
    return AverageRadius * centralAngleRad ; // meters
  }

  private toRadians(degrees: number): number {
    return degrees * Math.PI / 180;
  }
}

